export const RUNTIME_CONFIG_PATH = "/vida-cloud-app-config.json";

export type AppUrls = Record<string, string>;

export type RuntimeConfig = {
  cognitoDomain: string;
  cognitoClientId: string;
  oauthRedirectUri: string;
  oauthPostLogoutRedirectUri?: string;
  sessionBrokerUrl?: string;
  loginUrl?: string;
  publicBaseUrl?: string;
  appUrls?: AppUrls;
};

let runtime: RuntimeConfig | null = null;

/** Fallback URLs used only in dev when no runtime config is served. */
const DEFAULT_APP_URLS: AppUrls = {
  seas: "https://seas.oceany.ai",
  wave: "https://wave.oceany.ai",
  tide: "https://tide.oceany.ai",
  surf: "https://surf.oceany.ai",
  compass: "https://compass.oceany.ai",
  pulse: "https://pulse.oceany.ai",
  currents: "https://currents.oceany.ai",
  reef: "https://reef.oceany.ai",
};

function fromEnv(key: string): string {
  const raw = import.meta.env as Record<string, string | undefined>;
  return typeof raw[key] === "string" ? raw[key]!.trim() : "";
}

export async function ensureConfig(): Promise<void> {
  if (import.meta.env.DEV) return;
  if (runtime) return;
  const res = await fetch(RUNTIME_CONFIG_PATH, { cache: "no-store" });
  if (res.ok) {
    runtime = (await res.json()) as RuntimeConfig;
    return;
  }
  if (fromEnv("VITE_COGNITO_DOMAIN") && fromEnv("VITE_COGNITO_CLIENT_ID") && fromEnv("VITE_OAUTH_REDIRECT_URI")) {
    return;
  }
  throw new Error(`Config mancante: serve ${RUNTIME_CONFIG_PATH} o variabili VITE_*.`);
}

export function getCognitoDomain(): string {
  return (runtime?.cognitoDomain?.trim() || fromEnv("VITE_COGNITO_DOMAIN")).replace(/\/$/, "");
}

export function getClientId(): string {
  return runtime?.cognitoClientId?.trim() || fromEnv("VITE_COGNITO_CLIENT_ID");
}

export function getRedirectUri(): string {
  return runtime?.oauthRedirectUri?.trim() || fromEnv("VITE_OAUTH_REDIRECT_URI");
}

export function getSessionBrokerUrl(): string {
  return runtime?.sessionBrokerUrl?.trim() || fromEnv("VITE_SESSION_BROKER_URL");
}

export function getLoginUrl(): string {
  return runtime?.loginUrl?.trim() || fromEnv("VITE_LOGIN_URL");
}

export function getPostLogoutRedirect(): string {
  const login = getLoginUrl();
  if (login) return `${login.replace(/\/$/, "")}/login`;
  const fromRuntime = runtime?.oauthPostLogoutRedirectUri?.trim();
  if (fromRuntime) return fromRuntime;
  const envLogout = fromEnv("VITE_OAUTH_POST_LOGOUT_REDIRECT");
  if (envLogout) return envLogout;
  return "https://login.oceany.ai/login";
}

/** Public HTTPS base URL for each Oceany app, from runtime config with dev fallbacks. */
export function getAppUrls(): AppUrls {
  const merged: AppUrls = { ...DEFAULT_APP_URLS };
  const fromRuntime = runtime?.appUrls;
  if (fromRuntime && typeof fromRuntime === "object") {
    for (const [key, value] of Object.entries(fromRuntime)) {
      if (typeof value === "string" && value.trim()) merged[key] = value.trim().replace(/\/$/, "");
    }
  }
  return merged;
}

export function isConfigured(): boolean {
  return Boolean(getCognitoDomain() && getClientId());
}
